import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/common/Layout';
import { permissionService, Permission } from '../services/permissionService';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/common/Icon';
import { 
  HiOutlineShieldCheck, 
  HiOutlinePlus, 
  HiOutlineX
} from 'react-icons/hi';
import { FiEdit2, FiTrash2, FiSave } from 'react-icons/fi';

const Permissions: React.FC = () => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [groupedPermissions, setGroupedPermissions] = useState<{ [key: string]: Permission[] }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [formData, setFormData] = useState({ name: '', resource: '', action: '' });
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Only org_admin can access permissions
    if (user?.role !== 'org_admin') {
      setError('You do not have permission to access permissions');
      return;
    }

    // Prevent duplicate calls from React StrictMode
    if (hasLoadedRef.current) {
      return;
    }
    hasLoadedRef.current = true;
    loadData();
  }, [user]);

  const loadData = async () => {
    // Only org_admin can access permissions
    if (user?.role !== 'org_admin') {
      setError('You do not have permission to access permissions');
      setPermissions([]);
      setGroupedPermissions({});
      return;
    }

    // Prevent duplicate concurrent calls
    if (loading) {
      return;
    }
    try {
      setLoading(true);
      const data = await permissionService.getAll() as Permission[];
      setPermissions(data);
      
      // Group by resource
      const grouped = data.reduce((acc, perm) => {
        if (!acc[perm.resource]) {
          acc[perm.resource] = [];
        }
        acc[perm.resource].push(perm);
        return acc;
      }, {} as { [key: string]: Permission[] });
      setGroupedPermissions(grouped);
      setError('');
    } catch (err: any) {
      // Handle 403 Forbidden (permission denied) gracefully
      if (err.response?.status === 403) {
        setError('You do not have permission to access permissions');
        setPermissions([]);
        setGroupedPermissions({});
      } else {
        setError(err.response?.data?.message || 'Failed to load permissions');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPermission(null);
    setFormData({ name: '', resource: '', action: '' });
    setShowModal(true);
  };

  const handleEdit = (permission: Permission) => {
    setEditingPermission(permission);
    setFormData({
      name: permission.name,
      resource: permission.resource,
      action: permission.action
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.name.trim() || !formData.resource.trim() || !formData.action.trim()) {
        setError('All fields are required');
        return;
      }

      if (editingPermission) {
        await permissionService.update(editingPermission.id, formData);
      } else {
        await permissionService.create(formData);
      }

      setShowModal(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save permission');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this permission?')) {
      return;
    }

    try {
      await permissionService.delete(id);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete permission');
    }
  };

  const commonResources = [
    'dashboard', 'leads', 'customers', 'tenders', 'orders', 
    'kyc', 'csv_import', 'users', 'brands', 'settings', 
    'roles', 'permissions'
  ];

  const commonActions = ['view', 'create', 'edit', 'delete', 'assign', 'convert', 'upload', 'approve', 'manage', 'import'];

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-8">Loading permissions...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Permissions Management</h1>
            <p className="text-sm text-gray-600 mt-1">Manage system permissions</p>
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center space-x-2 bg-yellow-500 text-black-950 px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors font-bold"
          >
            <Icon component={HiOutlinePlus} className="w-5 h-5" />
            <span>Create Permission</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Grouped View */}
        <div className="space-y-4">
          {Object.entries(groupedPermissions).map(([resource, perms]) => (
            <div key={resource} className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 capitalize">
                <Icon component={HiOutlineShieldCheck} className="w-5 h-5 inline-block mr-2 text-yellow-600" />
                {resource.replace('_', ' ')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {perms.map((perm) => (
                  <div
                    key={perm.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{perm.name}</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {perm.resource}:{perm.action}
                        </p>
                      </div>
                      <div className="flex space-x-2 ml-2">
                        <button
                          onClick={() => handleEdit(perm)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Icon component={FiEdit2} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(perm.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Icon component={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Create/Edit Permission Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex justify-between items-center p-6 border-b">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingPermission ? 'Edit Permission' : 'Create Permission'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon component={HiOutlineX} className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Permission Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="e.g., View Data"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Resource *
                  </label>
                  <input
                    type="text"
                    list="resources"
                    value={formData.resource}
                    onChange={(e) => setFormData({ ...formData, resource: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="e.g., data"
                  />
                  <datalist id="resources">
                    {commonResources.map(resource => (
                      <option key={resource} value={resource} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Action *
                  </label>
                  <input
                    type="text"
                    list="actions"
                    value={formData.action}
                    onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="e.g., view"
                  />
                  <datalist id="actions">
                    {commonActions.map(action => (
                      <option key={action} value={action} />
                    ))}
                  </datalist>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Format:</strong> {formData.resource || 'resource'}:{formData.action || 'action'}
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 p-6 border-t">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 flex items-center space-x-2 font-bold"
                >
                  <Icon component={FiSave} className="w-4 h-4" />
                  <span>Save</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Permissions;




